import { useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

/** Matches `--duration-normal` so wizard steps speak the same motion. */
const FADE_MS = 240;

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Fade the current screen out, swap, then fade the next in.
 * Layout around the slot (auth chrome, brand panel) stays painted so the
 * page never flashes an unstyled blank. The active step stays live (typing
 * is not snapshotted) until a new stepKey arrives.
 */
export function StepFade({
  stepKey,
  appear = false,
  children,
}: {
  stepKey: string;
  appear?: boolean;
  children: ReactNode;
}) {
  const shownKeyRef = useRef(stepKey);
  const [shownKey, setShownKey] = useState(stepKey);
  const [visible, setVisible] = useState(!appear);
  const snapshot = useRef(children);
  const fadeTimer = useRef(0);
  const frame = useRef(0);
  const live = stepKey === shownKey;

  if (live) {
    snapshot.current = children;
  }

  useEffect(() => {
    if (stepKey === shownKeyRef.current) return;

    window.clearTimeout(fadeTimer.current);
    if (frame.current) cancelAnimationFrame(frame.current);

    const instant = prefersReducedMotion();
    const reveal = () => {
      shownKeyRef.current = stepKey;
      setShownKey(stepKey);
      if (instant) {
        setVisible(true);
        return;
      }
      frame.current = requestAnimationFrame(() => {
        frame.current = requestAnimationFrame(() => setVisible(true));
      });
    };

    if (instant) {
      reveal();
      return;
    }

    setVisible(false);
    fadeTimer.current = window.setTimeout(reveal, FADE_MS);
    return () => {
      window.clearTimeout(fadeTimer.current);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [stepKey]);

  useEffect(() => {
    if (!appear || prefersReducedMotion()) {
      setVisible(true);
      return;
    }
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, [appear]);

  return (
    <div
      className={cn('transition-opacity ease-soft', visible ? 'opacity-100' : 'pointer-events-none opacity-0')}
      style={{ transitionDuration: `${FADE_MS}ms` }}
    >
      {live ? children : snapshot.current}
    </div>
  );
}
